import React, { useEffect, useMemo, useState } from 'react'
import { ElementData, ElementDataValue, FormConfig } from '../../lib/types'
import FormInputElement from './input-element'
import { InputElementTypes, validateForm } from '../../lib/form-util'
import MarkdownElement from './markdown'
import FormCheckboxElement from './checkbox'
import FormSelectElement from './select'
import './elements.css'
import EntityElement from './entity'
import MultiEntityElement from './multi-entity'
import MagicPointerElement from './magic-pointer'
import MultiMagicPointerElement from './multi-magic-pointer'
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@carbon/react'
import { Edit, TrashCan } from '@carbon/icons-react'

type Props = {
    config: FormConfig
    data?: ElementData
    prevData?: ElementData
    similarRow?: ElementData
    destData?: any
    onChange?: (data: ElementData) => void
    readOnly?: boolean
    isNew?: boolean
    changes?: ElementData[]
    editRow?: (data: ElementData, index: number) => void
    deleteRow?: (index: number) => void
}

export default function Form(props: Props) {
    const { config, prevData, onChange, isNew, changes, editRow, deleteRow, destData } = props
    const [data, setData] = useState<ElementData>(Object.assign({}, props.data))
    const headers = useMemo(
        () =>
            config.elements.filter(
                (col) => col.type !== 'markdown' && col.type !== 'markdown-reviewer',
            ),
        [config.elements],
    )

    function updateData(field: string, value: ElementDataValue) {
        if (value === '') value = null
        const existingKey = Object.keys(data).find(key => key.toLowerCase() === field.toLowerCase());
        const keyToUse = existingKey || field;
        let newData = Object.assign({}, data, { [keyToUse]: value })
        setData(newData)
        onChange && onChange(newData)
    }

    function batchUpdateData(changes: Record<string, ElementDataValue>) {
        let newData = Object.assign({}, data, changes)
        setData(newData)
        onChange && onChange(newData)
    }

    function handleBooleanChange(field: string, value: ElementDataValue) {
        if (field.toLowerCase() === 'osi_no_match' && value === true) {
            const noMatchKey = Object.keys(data).find(k => k.toLowerCase() === 'osi_no_match') || field
            const dateKey = Object.keys(data).find(k => k.toLowerCase() === 'owner_begin_date')
            const updates: Record<string, ElementDataValue> = { [noMatchKey]: value }
            if (dateKey) {
                const dateInDest = destData && Object.keys(destData).some((k: string) => k.toLowerCase() === 'owner_begin_date')
                if (!dateInDest) updates[dateKey] = null
            }
            batchUpdateData(updates)
        } else {
            updateData(field, value)
        }
    }

    const effectiveData = data

    const effectiveOriginal = props.data || {}

    useEffect(() => {
        if (props.data) {
            setData(props.data)
        }
    }, [props.data])

    let [valid, errorMsg] = useMemo(() => validateForm(config), [config])
    if (!valid) {
        return <div className='text-red-500'>Invalid form config: {errorMsg}</div>
    }

    return (
        <div>
            {isNew && changes && changes.length > 0 && (
                <div className='my-2'>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {headers.map((col, i) => (
                                    <TableHeader key={i}>{col.label}</TableHeader>
                                ))}
                                <TableHeader>Notes</TableHeader>
                                <TableHeader>Sources</TableHeader>
                                <TableHeader>Actions</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {changes.map((data, i) => (
                                <TableRow key={i} className='cursor-default'>
                                    {headers.map((col, i) => (
                                        <TableCell key={col.field! + i}>
                                            {col.field ? data[col.field] : ''}
                                        </TableCell>
                                    ))}
                                    <TableCell>{data.note || ''}</TableCell>
                                    <TableCell>{data.sources || ''}</TableCell>
                                    <TableCell className='progressStep flexRow'>
                                        <Button
                                            kind='ghost'
                                            onClick={() => editRow && editRow(data, i)}
                                        >
                                            <Edit />
                                        </Button>{' '}
                                        <Button
                                            kind='ghost'
                                            onClick={() => deleteRow && deleteRow(i)}
                                        >
                                            <TrashCan />
                                        </Button>{' '}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
           
            {config.elements.map((element, i) => {
                if (props.readOnly) element = Object.assign({}, element, { readonly: true })
                if (InputElementTypes.includes(element.type))
                    return (
                        <FormInputElement
                            key={i}
                            element={element}
                            original={effectiveOriginal}
                            data={effectiveData}
                            prevData={prevData}
                            onChange={updateData}
                        />
                    )
                else if (element.type === 'boolean')
                    return (
                        <FormCheckboxElement
                            key={i}
                            element={element}
                            original={props.data || {}}
                            data={data}
                            prevData={prevData}
                            onChange={handleBooleanChange}
                        />
                    )
                else if (element.type === 'select')
                    return (
                        <FormSelectElement
                            key={i}
                            element={element}
                            original={props.data || {}}
                            data={data}
                            prevData={prevData}
                            onChange={updateData}
                        />
                    )
                else if (element.type === 'markdown')
                    return <MarkdownElement key={i} element={element} data={data} />
                else if (element.type === 'markdown-reviewer')
                    return (
                        <MarkdownElement
                            key={i}
                            element={element}
                            data={data}
                            reviewerOnly={true}
                        />
                    )
                else if (element.type === 'entity')
                    return (
                        <EntityElement
                            key={i}
                            element={element}
                            original={props.data || {}}
                            data={data}
                            prevData={prevData}
                            onChange={updateData}
                        />
                    )
                else if (element.type === 'multi-entity')
                    return (
                        <MultiEntityElement
                            key={i}
                            id={((props.data?.id || props.data?.ID) as number) || 0}
                            element={element}
                        />
                    )
                else if (element.type === 'magic-pointer')
                    return (
                        <MagicPointerElement
                            key={i}
                            element={element}
                            original={props.data || {}}
                            data={data}
                            prevData={prevData}
                            onChange={batchUpdateData}
                        />
                    )
                else if (element.type === 'multi-magic-pointer')
                    return (
                        <MultiMagicPointerElement
                            key={i}
                            id={((props.data?.id || props.data?.ID) as number) || 0}
                            element={element}
                        />
                    )
                return null
            })}
        </div>
    )
}
